/* ============================================================
   ManMade tracking engine (mm-tracking.js)

   Loaded (deferred) by snippets/mm-tracking.liquid on every
   layout. Reads its configuration from the JSON blob rendered
   at #mm-tracking-config, so this file contains no IDs.

   Responsibilities:
     1. Attribution — persist click IDs / UTMs (captured by the
        inline head script) onto the cart as attributes so every
        order carries its ad provenance (_fbc/_fbp/_gclid +
        _mm_attribution JSON). This feeds Meta CAPI matching and
        Google offline-conversion imports even when ad blockers
        stop the browser pixels.
     2. Funnel events — ViewContent/view_item, AddToCart/
        add_to_cart, Search/search, ViewCategory/view_item_list,
        optional InitiateCheckout/begin_checkout. Only dispatched
        to networks explicitly enabled in theme settings; the
        Shopify channel apps (Meta channel, Google & YouTube)
        keep firing independently — see docs/TRACKING.md for the
        dedup matrix before enabling anything here.
     3. Interception — add-to-cart is detected from the actual
        /cart/add.js network responses (fetch + XHR + plain form
        fallback), so values/quantities are the real post-discount
        amounts Shopify returned, not what the button hoped for.
     4. Button journey — every CTA click (nav, drawer, cart,
        upsells, carousels, checkout) is auto-labelled
        placement|action and the compact click path rides the cart
        as _mm_buttons, so each order shows which buttons produced
        it. This is the internal-campaign answer that utm_* params
        must NOT be used for: internal UTMs restart GA4 sessions
        and overwrite the ad's click IDs in mm_attr, and most CTAs
        here are JS actions that never navigate anyway.

   Every entry point is wrapped so a failure here can never break
   shopping. Interceptors are strictly pass-through: the original
   fetch/XHR behavior is returned untouched.

   Debugging: append ?mm_debug=1 to any URL (persists for the
   session) and watch the console. window.MMTracking exposes
   getAttribution() and track().
   ============================================================ */
(function () {
  'use strict';
  // Consent gate: snippets/mm-consent.liquid decides when tracking may run
  // (immediately outside restricted regions; after Accept in California) and
  // calls window.mmTrackingStart(), which fires 'mm:tracking-start'.
  if (window.__mmTrackingStarted) boot();
  else document.addEventListener('mm:tracking-start', boot, { once: true });

  function boot() {
  if (window.__mmTrackingLoaded) return;
  window.__mmTrackingLoaded = true;

  /* ---------- config ---------- */
  var cfg;
  try {
    var el = document.getElementById('mm-tracking-config');
    cfg = el ? JSON.parse(el.textContent) : null;
  } catch (e) { cfg = null; }
  if (!cfg) return;

  var DEBUG = false;
  try {
    if (/[?&]mm_debug=1/.test(location.search)) sessionStorage.setItem('mm_debug', '1');
    if (/[?&]mm_debug=0/.test(location.search)) sessionStorage.removeItem('mm_debug');
    DEBUG = sessionStorage.getItem('mm_debug') === '1';
  } catch (e) { /* storage unavailable */ }

  function log() {
    if (!DEBUG) return;
    try {
      var args = ['[mm-tracking]'].concat([].slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) { /* noop */ }
  }

  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); } catch (e) { log('error', e); }
    };
  }

  /* ---------- small utils ---------- */
  function uuid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) { /* fall through */ }
    var d = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? decodeURIComponent(m[2]) : '';
    } catch (e) { return ''; }
  }

  function readAttr() {
    try { return JSON.parse(localStorage.getItem('mm_attr') || 'null'); } catch (e) { return null; }
  }

  /* Consent: if Shopify's Customer Privacy API is present AND says no,
     hold marketing events. When it is absent (no consent banner
     configured — current state of this store) everything is allowed,
     matching how the channel app pixels already behave. */
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed() !== false;
    } catch (e) { /* treat as allowed */ }
    return true;
  }

  /* ---------- network dispatch ---------- */
  var metaOn = !!(cfg.meta && cfg.meta.pixelId);
  var gaOn = !!(cfg.ga && cfg.ga.ga4Id);

  function contentId(productId, variantId) {
    var fmt = (cfg.meta && cfg.meta.idFormat) || 'retailer';
    if (fmt === 'product') return String(productId || variantId);
    if (fmt === 'variant') return String(variantId || productId);
    return 'shopify_' + (cfg.shop.countryCode || 'US') + '_' + productId + '_' + variantId;
  }

  var fired = {}; // in-page dedupe: key -> timestamp

  function dedupe(key, windowMs) {
    var now = Date.now();
    if (fired[key] && (windowMs === 0 || now - fired[key] < windowMs)) return true;
    fired[key] = now;
    return false;
  }

  /* evt = { key, window (0 = once per page), meta:{name, custom, params}, ga:{name, params} } */
  function emit(evt) {
    try {
      if (evt.key && dedupe(evt.key, evt.window || 0)) { log('deduped', evt.key); return; }
      if (!marketingAllowed()) { log('blocked by consent', evt); return; }
      if (metaOn && evt.meta && cfg.meta.events[evt.meta.gate] !== false && typeof window.fbq === 'function') {
        var eventID = uuid();
        // trackSingle, never track: the Meta channel app pixel shares this
        // main-frame fbq — a broadcast would also hit its pixel.
        window.fbq(evt.meta.custom ? 'trackSingleCustom' : 'trackSingle', cfg.meta.pixelId, evt.meta.name, evt.meta.params, { eventID: eventID });
        log('fbq', evt.meta.name, evt.meta.params, eventID);
      }
      if (gaOn && evt.ga && cfg.ga.events[evt.ga.gate] !== false && typeof window.gtag === 'function') {
        window.gtag('event', evt.ga.name, evt.ga.params);
        log('gtag', evt.ga.name, evt.ga.params);
      }
    } catch (e) { log('emit error', e); }
  }

  /* ---------- cart attribute stamping ---------- */
  var lastKnownCart = cfg.cart || {};

  function attributionAttrs() {
    var attr = readAttr() || {};
    var out = {};
    var fbc = getCookie('_fbc');
    var fbp = getCookie('_fbp');
    if (fbc) out._fbc = fbc;
    if (fbp) out._fbp = fbp;
    var last = attr.last || {};
    if (last.gclid) out._gclid = last.gclid;
    try {
      var blob = JSON.stringify({
        first: attr.first || null,
        last: attr.last || null,
        v: 1
      });
      if (blob.length <= 2000) out._mm_attribution = blob;
    } catch (e) { /* skip blob */ }
    var journey = journeyString();
    if (journey) out._mm_buttons = journey;
    return out;
  }

  var syncTimer = null;
  /* immediate=true skips the debounce and sends with keepalive — used on
     checkout intent, where the navigation would otherwise cancel both the
     400ms timer and an ordinary fetch. */
  function syncCartAttributes(reason, immediate) {
    if (!cfg.attribution || !cfg.attribution.cartSync) return;
    clearTimeout(syncTimer);
    var run = safe(function () {
      var attrs = attributionAttrs();
      var keys = Object.keys(attrs);
      if (!keys.length) return;
      var fingerprint = '';
      try { fingerprint = (lastKnownCart.token || '') + '|' + JSON.stringify(attrs); } catch (e) { /* best effort */ }
      try {
        if (fingerprint && localStorage.getItem('mm_attr_synced') === fingerprint) { log('cart attrs already synced'); return; }
      } catch (e) { /* storage unavailable */ }
      var payload = {};
      keys.forEach(function (k) { payload[k] = attrs[k]; });
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: payload }),
        keepalive: !!immediate
      }).then(function (res) {
        if (!res.ok) return;
        res.json().then(safe(function (cart) {
          lastKnownCart = { token: cart.token, count: cart.item_count, total: cart.total_price };
          try { localStorage.setItem('mm_attr_synced', (cart.token || '') + '|' + JSON.stringify(attrs)); } catch (e) { /* noop */ }
          log('cart attributes synced (' + reason + ')', payload);
        }));
      }).catch(function () { /* silent: attribution must never surface errors */ });
    });
    if (immediate) run();
    else syncTimer = setTimeout(run, 400);
  }

  /* ---------- add-to-cart handling ---------- */
  function normalizeAdded(data) {
    if (!data) return [];
    var items = data.items || [data];
    return items.map(function (it) {
      return {
        productId: it.product_id,
        variantId: it.variant_id || it.id,
        title: it.product_title || it.title || '',
        variantTitle: it.variant_title || '',
        quantity: it.quantity || 1,
        // /cart/add.js returns money in cents; prefer the final
        // (post-discount) amounts so ad platforms optimize on truth.
        lineTotal: (typeof it.final_line_price === 'number' ? it.final_line_price
          : (it.final_price || it.price || 0) * (it.quantity || 1)) / 100,
        unitPrice: (it.final_price || it.price || 0) / 100
      };
    }).filter(function (it) { return it.variantId; });
  }

  function handleAdd(items, source) {
    if (!items.length) return;
    var key = 'atc:' + items.map(function (it) { return it.variantId + 'x' + it.quantity; }).sort().join(',');
    if (dedupe(key, 1500)) { log('atc deduped', key, source); return; }
    var value = 0;
    items.forEach(function (it) { value += it.lineTotal; });
    value = Math.round(value * 100) / 100;
    var ids = items.map(function (it) { return contentId(it.productId, it.variantId); });
    emit({
      meta: {
        name: 'AddToCart', gate: 'addtocart',
        params: {
          content_ids: ids,
          content_type: 'product',
          content_name: items[0].title,
          contents: items.map(function (it) { return { id: contentId(it.productId, it.variantId), quantity: it.quantity, item_price: it.unitPrice }; }),
          num_items: items.reduce(function (n, it) { return n + it.quantity; }, 0),
          value: value,
          currency: cfg.shop.currency
        }
      },
      ga: {
        name: 'add_to_cart', gate: 'add_to_cart',
        params: {
          currency: cfg.shop.currency,
          value: value,
          items: items.map(function (it) {
            return {
              item_id: contentId(it.productId, it.variantId),
              item_name: it.title,
              item_variant: it.variantTitle,
              price: it.unitPrice,
              quantity: it.quantity
            };
          })
        }
      }
    });
    log('AddToCart (' + source + ')', items, value);
    syncCartAttributes('add-to-cart');
  }

  var CART_ADD = /\/cart\/add(\.js)?([?#]|$)/;

  /* fetch interceptor — pass-through; we only observe a clone of
     successful /cart/add responses. */
  (function wrapFetch() {
    if (!window.fetch || window.fetch.__mmWrapped) return;
    var orig = window.fetch;
    var wrapped = function () {
      var args = arguments;
      var url = '';
      try { url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || ''; } catch (e) { /* noop */ }
      var p = orig.apply(this, args);
      try {
        if (CART_ADD.test(url)) {
          p.then(function (res) {
            if (res && res.ok) {
              res.clone().json().then(safe(function (data) {
                handleAdd(normalizeAdded(data), 'fetch');
              })).catch(function () { /* non-JSON response */ });
            }
          }).catch(function () { /* request failed; nothing to track */ });
        }
      } catch (e) { log('fetch observe error', e); }
      return p;
    };
    wrapped.__mmWrapped = true;
    window.fetch = wrapped;
  })();

  /* XHR interceptor — same contract, for apps that still use XHR. */
  (function wrapXHR() {
    if (!window.XMLHttpRequest) return;
    var proto = XMLHttpRequest.prototype;
    if (proto.open.__mmWrapped) return;
    var origOpen = proto.open;
    var origSend = proto.send;
    proto.open = function (method, url) {
      try { this.__mmUrl = url; } catch (e) { /* noop */ }
      return origOpen.apply(this, arguments);
    };
    proto.open.__mmWrapped = true;
    proto.send = function () {
      try {
        var xhr = this;
        if (xhr.__mmUrl && CART_ADD.test(String(xhr.__mmUrl))) {
          xhr.addEventListener('load', safe(function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              handleAdd(normalizeAdded(JSON.parse(xhr.responseText)), 'xhr');
            }
          }));
        }
      } catch (e) { log('xhr observe error', e); }
      return origSend.apply(this, arguments);
    };
  })();

  /* Plain form-submit fallback: only fires when no JS handler
     prevented the default navigation (i.e. the fetch/XHR paths
     above will NOT see this add). Values come from the rendered
     product config, flagged estimated in debug. */
  document.addEventListener('submit', function (e) {
    try {
      var form = e.target;
      // getAttribute, not form.action: an <input name="action"> would clobber the property
      var action = form && form.getAttribute ? (form.getAttribute('action') || '') : '';
      if (!CART_ADD.test(action)) return;
      setTimeout(safe(function () {
        if (e.defaultPrevented) return; // JS took over; interceptors cover it
        var fd = new FormData(form);
        var variantId = parseInt(fd.get('id'), 10);
        var qty = parseInt(fd.get('quantity'), 10) || 1;
        if (!variantId) return;
        var price = 0, productId = null, title = '';
        if (cfg.product) {
          productId = cfg.product.id;
          title = cfg.product.title;
          (cfg.product.variants || []).forEach(function (v) {
            if (v.id === variantId) price = v.price;
          });
        }
        handleAdd([{
          productId: productId, variantId: variantId, title: title,
          variantTitle: '', quantity: qty,
          lineTotal: price * qty, unitPrice: price
        }], 'form');
      }), 0);
    } catch (err) { log('submit observe error', err); }
  }, true);

  /* ---------- checkout intent (optional, default OFF) ---------- */
  function checkoutParams() {
    return {
      value: (lastKnownCart.total || 0) / 100,
      currency: cfg.shop.currency,
      num_items: lastKnownCart.count || 0
    };
  }

  var CHECKOUT_INTENT = '[name="checkout"], [data-action="checkout"], [data-mm-action="checkout"], ' +
    'a[href*="/checkout"], [data-action="buy-now"], [data-mm-action="buy-now"]';

  document.addEventListener('click', function (e) {
    try {
      var t = e.target && e.target.closest && e.target.closest(CHECKOUT_INTENT);
      if (!t) return;
      emit({
        key: 'initiate-checkout', window: 3000,
        meta: { name: 'InitiateCheckout', gate: 'initiate_checkout', params: checkoutParams() },
        ga: {
          name: 'begin_checkout', gate: 'begin_checkout',
          params: { value: (lastKnownCart.total || 0) / 100, currency: cfg.shop.currency }
        }
      });
    } catch (err) { log('checkout observe error', err); }
  }, true);

  /* ---------- button journey ----------
     One delegated capture-phase listener labels every CTA click as
     placement|action and keeps the last 12 steps in localStorage; the
     compact path is stamped onto the cart as _mm_buttons by
     attributionAttrs(), so it rides every existing sync (landing,
     add-to-cart, checkout intent) and lands on the order.

     Overrides in markup, no JS needed:
       data-mm-track="my-label"  on a button/link (or ancestor) names it
       data-mm-track="off"       excludes a subtree entirely */
  var BTN_STORE = 'mm_btn_path';

  function readButtons() {
    try {
      var s = JSON.parse(localStorage.getItem(BTN_STORE) || 'null');
      if (s && s.v === 1 && s.path && s.path.length !== undefined) return s;
    } catch (e) { /* corrupted — start fresh */ }
    return { v: 1, path: [] };
  }

  /* Placement zones, most specific first — the peek popup sits outside the
     cart drawer in the DOM but a card inside the drawer opened it, so it
     gets its own name rather than inheriting one. */
  var BTN_ZONES = [
    ['#mm-addon-peek', 'peek'],
    ['[data-mm-addons]', 'cart-addons'],
    ['#mm-cart', 'cart'],
    ['[data-mm-pcar]', 'carousel'],
    ['#mm-sticky-atc', 'sticky-atc'],
    ['.mm-drawer', 'nav'],
    ['header', 'nav'],
    ['footer', 'footer']
  ];

  function buttonPlacement(el) {
    for (var i = 0; i < BTN_ZONES.length; i++) {
      if (el.closest(BTN_ZONES[i][0])) return BTN_ZONES[i][1];
    }
    var sec = el.closest('.shopify-section');
    if (sec && sec.id) {
      return sec.id.replace(/^shopify-section-/, '').replace(/^template--[^_]+__/, '').slice(0, 24);
    }
    return (cfg.page && cfg.page.type) || 'page';
  }

  function buttonLabel(el) {
    var custom = el.closest('[data-mm-track]');
    if (custom) return String(custom.getAttribute('data-mm-track')).slice(0, 48);
    var action = el.getAttribute('data-mm-action') || '';
    if (!action) {
      var href = el.getAttribute ? el.getAttribute('href') : '';
      if (href && href.charAt(0) !== '#') action = String(href).split('?')[0].slice(0, 40);
    }
    var text = (el.getAttribute('aria-label') || (el.dataset && el.dataset.title) || el.textContent || '')
      .replace(/\s+/g, ' ').trim().slice(0, 28);
    return action && text ? action + ':' + text : (action || text || 'click');
  }

  function journeyString() {
    var path = readButtons().path;
    if (!path.length) return '';
    var s = path.map(function (b) { return b.p + '|' + b.id; }).join(' > ');
    // Over budget: drop the oldest steps, keep the recent ones.
    while (s.length > 700 && path.length > 1) {
      path = path.slice(1);
      s = path.map(function (b) { return b.p + '|' + b.id; }).join(' > ');
    }
    return s.slice(0, 700);
  }

  document.addEventListener('click', safe(function (e) {
    if (!e.target || !e.target.closest) return;
    var el = e.target.closest('a[href], button, [role="button"], [data-mm-action]');
    if (!el) return;
    if (el.closest('[data-mm-track="off"]')) return;
    // Carousel dots/arrows and gallery chrome are browsing, not CTA intent.
    if (el.closest('.mm-pcar__dot, .mm-pcar__arrow, .mm-apk__dot, .mm-cart__addons-dots, .mm-hero__thumb, .mm-hero__arrow')) return;

    var entry = { t: Date.now(), p: buttonPlacement(el), id: buttonLabel(el) };
    var store = readButtons();
    var last = store.path[store.path.length - 1];
    // Collapse rage-clicks on the same control into one step.
    if (last && last.p === entry.p && last.id === entry.id && entry.t - last.t < 2000) last.t = entry.t;
    else store.path.push(entry);
    if (store.path.length > 12) store.path = store.path.slice(-12);
    try { localStorage.setItem(BTN_STORE, JSON.stringify(store)); } catch (err) { /* private mode */ }
    log('button', entry.p + '|' + entry.id);

    // Checkout is the last chance to stamp the journey before leaving the
    // storefront — flush immediately (keepalive survives the navigation).
    // Plain adds don't need this: handleAdd() syncs after the response.
    if (el.closest(CHECKOUT_INTENT)) syncCartAttributes('checkout-intent', true);
  }), true);

  /* ---------- page-scoped events ---------- */
  safe(function pageEvents() {
    var page = cfg.page || {};

    if (page.type === 'product' && cfg.product) {
      var p = cfg.product;
      emit({
        key: 'viewcontent:' + p.id,
        meta: {
          name: 'ViewContent', gate: 'viewcontent',
          params: {
            content_ids: [contentId(p.id, p.selectedVariantId)],
            content_type: 'product',
            content_name: p.title,
            content_category: p.type || '',
            value: p.price,
            currency: cfg.shop.currency
          }
        },
        ga: {
          name: 'view_item', gate: 'view_item',
          params: {
            currency: cfg.shop.currency,
            value: p.price,
            items: [{
              item_id: contentId(p.id, p.selectedVariantId),
              item_name: p.title,
              item_brand: p.vendor || '',
              item_category: p.type || '',
              price: p.price,
              quantity: 1
            }]
          }
        }
      });
    }

    if (page.type === 'collection' && cfg.collection) {
      var c = cfg.collection;
      emit({
        key: 'viewcategory:' + c.id,
        meta: {
          name: 'ViewCategory', gate: 'viewcategory', custom: true,
          params: {
            content_name: c.title,
            content_category: c.title,
            content_type: 'product',
            content_ids: (c.items || []).slice(0, 10).map(function (it) { return contentId(it.id, it.variantId); })
          }
        },
        ga: {
          name: 'view_item_list', gate: 'view_item_list',
          params: {
            item_list_id: c.handle,
            item_list_name: c.title,
            items: (c.items || []).map(function (it, i) {
              return {
                item_id: contentId(it.id, it.variantId),
                item_name: it.title,
                price: it.price,
                index: i,
                item_list_id: c.handle,
                item_list_name: c.title
              };
            })
          }
        }
      });
    }

    if (page.type === 'search' && page.searchTerms) {
      emit({
        key: 'search:' + page.searchTerms,
        meta: {
          name: 'Search', gate: 'search',
          params: { search_string: page.searchTerms, content_type: 'product' }
        },
        ga: { name: 'search', gate: 'search', params: { search_term: page.searchTerms } }
      });
    }

    if (page.type === 'cart') {
      emit({
        key: 'view_cart',
        ga: {
          name: 'view_cart', gate: 'view_cart',
          params: { currency: cfg.shop.currency, value: (lastKnownCart.total || 0) / 100 }
        }
      });
    }
  })();

  /* ---------- initial attribution sync ----------
     Stamp attribution onto an existing cart on landing (covers
     returning visitors whose cart predates this visit's click IDs). */
  if ((lastKnownCart.count || 0) > 0) syncCartAttributes('landing');

  /* ---------- public API ---------- */
  window.MMTracking = {
    version: '1.1.0',
    getAttribution: function () {
      return { stored: readAttr(), fbc: getCookie('_fbc'), fbp: getCookie('_fbp') };
    },
    getButtons: function () {
      return { path: readButtons().path, attr: journeyString() };
    },
    track: function (name, params) {
      emit({
        meta: { name: name, gate: '__custom', custom: true, params: params || {} },
        ga: { name: name, gate: '__custom', params: params || {} }
      });
    },
    syncCartAttributes: function () { syncCartAttributes('manual'); },
    /* Header search dropdown (mm-search.js): same Search / search events the
       /search page fires, deduped by the same key. */
    search: function (term) {
      term = String(term || '').trim();
      if (!term) return;
      emit({
        key: 'search:' + term,
        meta: { name: 'Search', gate: 'search', params: { search_string: term, content_type: 'product' } },
        ga: { name: 'search', gate: 'search', params: { search_term: term } }
      });
    },
    debug: DEBUG
  };

  log('engine ready', {
    meta: metaOn, ga: gaOn,
    attributionSync: !!(cfg.attribution && cfg.attribution.cartSync),
    page: cfg.page && cfg.page.type
  });
  }
})();
